import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from '../../../../../../ui/toast';
import {
  exportCandidateCSV,
  filterPreScreenAnswers,
  getAssessmentUsersResult,
  sendCandidateEmail,
  setCandidateStatus,
} from '../../../../../../utils/api_service/assessment_api';
import {
  editJobApplicant,
  getJobApplicants,
} from '../../../../../../utils/api_service/JobOpenings';
import {
  AssessmentUser,
  AttemptSkill,
  CandidateStatus,
  FilterState,
  PreScreenQuestion,
  UserAssessmentResponse } from '../../../../../../utils/types/assessment_types';
export interface IAssessmentResultsProps {
  assessmentId: string;
  userResults: UserAssessmentResponse[];
  highestScore: number;
  countCompleted: number;
  countTotal: number;
  completionRatio: string;
  candidateStatus?: CandidateStatus;
  updateAssessmentUserList?: (
    selectedRows: AssessmentUser[],
    candidateStatus: CandidateStatus
  ) => void;
  preScreenQuestions?: PreScreenQuestion[];
  skills?: string[];
  behaviorSkills?: string[];
  isAutoAssessment?: boolean;
  autoAssessmentRoute?: string;
  jobOpeningId?: string; // Required for bulk move operations with job applicants
  onResultClick?: (assessmentResult: any) => void; // Custom handler for row clicks in auto-assessment mode
}

export interface ISkillSelected {
  selected: boolean;
  skillName: string;
  skillId: string;
  index: number | undefined;
  displayLapsedTime: boolean;
}

export interface IResultListData {
  completionRatio: string;
  highestScore: number;
}

export const useAssessmentResultList = (props: IAssessmentResultsProps) => {
  const { id: jobOpeningId } = useParams<{ id: string }>();

  const {
    assessmentId,
    userResults,
    highestScore,
    countCompleted,
    countTotal,
    completionRatio,
    candidateStatus,
    updateAssessmentUserList,
    preScreenQuestions,
    skills,
    behaviorSkills,
    onResultClick, // Destructure the custom handler
  } = props;

  // State for managing visible skills and show all toggle
  const [visibleSkills, setVisibleSkills] = useState<Set<string>>(new Set());
  const [showAllSkills, setShowAllSkills] = useState(false);

  // Toggle individual skill visibility
  const toggleSkillVisibility = (skillId: string) => {
    setVisibleSkills(prev => {
      const newSet = new Set(prev);
      if (newSet.has(skillId)) {
        newSet.delete(skillId);
      } else {
        newSet.add(skillId);
      }
      return newSet;
    });
  };

  // Toggle visibility of all skills
  const toggleAllSkills = () => {
    setShowAllSkills(prev => !prev);
    if (!showAllSkills) {
      // Add all skills when enabling
      const allSkills = new Set([
        ...(skills || []),
        ...(behaviorSkills || []),
      ]);
      setVisibleSkills(allSkills);
    } else {
      // Clear all skills when disabling
      setVisibleSkills(new Set());
    }
  };

  const [filters, setFilters] = useState<FilterState[]>([]);
  const [resultList, setResultList] = useState<AssessmentUser[]>([]);
  const [storedResultList, setStoredResultList] = useState<AssessmentUser[]>(
    [],
  );
  const [skillVisibility, setSkillVisibility] = useState<boolean>(false);
  const [resultListData, setResultListData] = useState<IResultListData>();
  const [completionFilter, setCompletionFilter] = useState<boolean>(false);
  const [ascending, setAscending] = useState<boolean>(false);
  const [nameActive, setNameActive] = useState<boolean>(false);
  const [dateActive, setDateActive] = useState<boolean>(false);
  const [scoreActive, setScoreActive] = useState<boolean>(false);
  const [filterDrawer, setFilterDrawer] = useState<boolean>(false);
  const [skillSelected, setSkillSelected] = useState<ISkillSelected>({
    selected: false,
    skillName: '',
    skillId: '',
    index: undefined,
    displayLapsedTime: false,
  });
  const [selectedRows, setSelectedRows] = useState<AssessmentUser[]>([]);
  const userAssessmentIds = userResults.map((item) => item.id);
  const [userAssessmentId, setUserAssessmentId] =
    useState<string[]>(userAssessmentIds);
  const history = useHistory();
  const [selectionDropdownOpen, setSelectionDropdownOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [bulkActionOpen, setBulkActionOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSelectAllItems = (type: 'all' | 'page') => {
    const filteredData = getFilteredUsers();
    const transformedData = transformUserResultsToApiFormat(filteredData);
    const currentPageData = transformedData.slice(
      (pageNumber - 1) * 10,
      pageNumber * 10,
    );

    if (type === 'all') {
      if (selectedItems.length === transformedData.length) {
        setSelectedItems([]);
      } else {
        setSelectedItems(transformedData.map((item) => item.userId));
      }
    } else {
      const currentPageIds = new Set(
        currentPageData.map((item) => item.userId),
      );
      const selectedSet = new Set(selectedItems);
      const isCurrentPageFullySelected = Array.from(currentPageIds).every(
        (id) => selectedSet.has(id),
      );

      if (isCurrentPageFullySelected) {
        setSelectedItems([]);
      } else {
        const newSelection = new Set(selectedItems);
        currentPageIds.forEach((id) => newSelection.add(id));
        setSelectedItems(Array.from(newSelection));
      }
    }
    setSelectionDropdownOpen(false);
  };

  // Transform UserAssessmentResponse to API format for columns
  const transformUserResultsToApiFormat = (
    userResults: UserAssessmentResponse[],
  ) => {
    const transformedData = userResults.map((user) => ({
      enabled: user.enabled,
      score: user.score,
      status: user.status,
      marked: user.marked,
      user: {
        email: user.user.email,
        id: user.user.id,
        password: null,
        firstName: user.user.firstName,
        lastName: user.user.lastName,
        failedRetry: 0,
        isVerified: true,
        isLocked: false,
        passwordHistory: [],
      },
      notifications: {
        invite: true,
        unsuccessful: false,
        waitlisted: user.candidateStatus === 'Waitlisted',
        shortlisted: user.candidateStatus === 'Shortlisted',
      },
      assessment: {
        _cls: 'MongoAssessments.MongoCandidateAssessment',
        description: '',
        id: user.assessmentId,
        assessmentType: 'candidate',
        assessmentTitle: 'Assessment',
        startDate: user.startTime || 0,
        endDate: user.endTime || 0,
        isActive: true,
        jobPosition: 'Undefined',
        organizationId: '',
        userId: user.userId,
        creationDateTime: Date.now() / 1000,
        isStrictDuration: true,
        totalTime: 210,
        totalScore: user.maxScore || 0,
        preScreen: [],
      },
      id: user.id,
      userId: user.userId,
      assessmentId: user.assessmentId,
      startTime: user.startTime || 0,
      candidateStatus: user.candidateStatus,
      endTime: user.endTime || 0,
      totalTime:
        user.endTime && user.startTime ? user.endTime - user.startTime : 0,
      totalScore: user.score,
      behaviorScore: user.behaviorScore || 0, // Use the actual behavior score from the API response
      behaviorScores: user.behaviorScores || {},
      preScreenCompleted: user.preScreenCompleted || false,
      maxScore: user.maxScore || 0,
      skills: user.skills
        ? user.skills.map((skill) => ({
          skillId: skill.skillId,
          skillName: skill.skillName,
          timeTaken: skill.timeTaken || '0:00:00',
          timeTakenForSkill: skill.timeTakenForSkill || 0,
          timeExpiredCount: skill.timeExpiredCount || 0,
          correctCount: skill.correctCount || 0,
          totalCount: skill.totalCount || 0,
        }))
        : [],
    }));

    return transformedData;
  };

  // Calculate average completion time
  const calculateAverageTime = (): string => {
    const completedUsers = userResults.filter(
      (user) => user.endTime && user.startTime,
    );
    if (completedUsers.length === 0) return '00:00:00';

    const totalTime = completedUsers.reduce(
      (sum, user) => sum + ((user.endTime || 0) - (user.startTime || 0)),
      0,
    );
    const avgTimeInSeconds = Math.floor(totalTime / completedUsers.length);

    const hours = Math.floor(avgTimeInSeconds / 3600);
    const minutes = Math.floor((avgTimeInSeconds % 3600) / 60);
    const seconds = avgTimeInSeconds % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const handleOpenFilter = () => {
    setFilterOpen(true);
  };
  const handleCloseFilter = () => {
    setFilterOpen(false);
  };
  // Pagination and loading states
  const [loading, setLoading] = useState<boolean>(false);
  const [hasFetchedOnce, setHasFetchedOnce] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [sort, setSort] = useState<Record<string, 'asc' | 'desc'>>({
    score: 'desc',
  });

  // Server-side data
  const [serverUserResults, setServerUserResults] = useState<
    UserAssessmentResponse[]
  >([]);
  const [serverTotalCount, setServerTotalCount] = useState<number>(0);
  const [serverCurrentPage, setServerCurrentPage] = useState<number>(1);
  const [serverPageSize] = useState<number>(20);

  // Fetch data from server with filters
  const fetchAssessmentUsers = async (
    page: number = 1,
    sortConfig?: Record<string, 'asc' | 'desc'>,
    filters: FilterState[] = [],
  ) => {
    try {
      setLoading(true);

      // Use provided sort config or current sort state
      const currentSort = sortConfig || sort;

      // Convert sort object to sort string
      const sortString =
        Object.keys(currentSort).length > 0
          ? Object.entries(currentSort)
            .map(([key, value]) => (value === 'desc' ? `-${key}` : key))
            .join(',')
          : undefined;

      const response = await getAssessmentUsersResult(
        assessmentId,
        candidateStatus,
        sortString,
        page,
        serverPageSize,
        filters,
      );

      if (response.ok) {
        const data = await response.json();

        setServerUserResults(data.assessmentUserList || []);
        setServerTotalCount(data.totalCount || 0);
        setServerCurrentPage(page);
        setTotalPages(Math.ceil((data.totalCount || 0) / serverPageSize));
        setTotalRecords(data.totalCount || 0);
        setHasFetchedOnce(true);
      } else {
        toast.error('Failed to fetch assessment results', {
          position: 'bottom-center',
        });
      }
    } catch (error) {
      toast.error('Error fetching assessment results: ' + error, {
        position: 'bottom-center',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSortChange = (key: string, direction: 'asc' | 'desc') => {
    // All columns now use server-side sorting
    setSort((prev) => {
      // Clear all other sorts and set the new one
      const newSort: Record<string, 'asc' | 'desc'> = { [key]: direction };

      // Fetch data with new sort immediately
      setTimeout(() => {
        fetchAssessmentUsers(1, newSort);
      }, 0);

      setPageNumber(1);
      return newSort;
    });
  };

  const onPageChange = (page: number) => {
    setPageNumber(page);
    // Reset selected rows when changing pages
    setSelectedRows([]);
    // Fetch data for new page with current sort
    fetchAssessmentUsers(page, sort, filters);
  };

  // Initialize server data on mount and when key dependencies change
  useEffect(() => {
    if (assessmentId) {
      // Default sort to Skills Score descending (highest to lowest)
      fetchAssessmentUsers(1, { score: 'desc' }, filters);
      setSort({ score: 'desc' });
    }
  }, [assessmentId, candidateStatus]); // Removed userResults dependency to focus on server data

  useEffect(() => {
    // Set basic result data from props
    if (userResults.length !== 0) {
      setResultListData({
        highestScore: highestScore,
        completionRatio: completionRatio,
      });
    }
  }, [userResults, highestScore, completionRatio]);
  console.log('highestScore', highestScore);
  const filterSkill = (index: number, skill: AttemptSkill) => {
    if (skill.skillId === skillSelected.skillId) {
      setResultList(storedResultList);
      setSkillSelected({
        selected: false,
        skillName: '',
        skillId: '',
        index: undefined,
        displayLapsedTime: false,
      });
    } else {
      setResultList((resultListCopy) =>
        resultListCopy.sort((compareOne, compareTwo) => {
          const skillOne = compareOne.skills?.[index];
          const skillTwo = compareTwo.skills?.[index];
          return (skillTwo?.correctCount ?? 0) - (skillOne?.correctCount ?? 0);
        }),
      );
      setSkillSelected({
        selected: true,
        skillName: skill.skillName,
        skillId: skill.skillId,
        index: index,
        displayLapsedTime: false,
      });
    }
  };

  const filterCompletionTime = () => {
    if (!completionFilter) {
      if (skillSelected.skillName && skillSelected.index !== undefined) {
        setSkillSelected({
          ...skillSelected,
          displayLapsedTime: true,
        });

        setResultList((resultListCopy) =>
          resultListCopy.sort((compareOne, compareTwo) => {
            const skillOne = compareOne.skills?.[skillSelected.index!];
            const skillTwo = compareTwo.skills?.[skillSelected.index!];
            return (
              (skillOne?.timeTakenForSkill ?? 0) -
              (skillTwo?.timeTakenForSkill ?? 0)
            );
          }),
        );
        setCompletionFilter(true);
      } else {
        setResultList((resultListCopy) =>
          resultListCopy.sort(
            (compareOne, compareTwo) =>
              (compareOne.completionTime ?? 0) -
              (compareTwo.completionTime ?? 0),
          ),
        );
        setCompletionFilter(true);
      }
    } else if (
      skillSelected.displayLapsedTime &&
      skillSelected.index !== undefined
    ) {
      setResultList((resultListCopy) =>
        resultListCopy.sort((compareOne, compareTwo) => {
          const skillOne = compareOne.skills?.[skillSelected.index!];
          const skillTwo = compareTwo.skills?.[skillSelected.index!];
          return (skillTwo?.correctCount ?? 0) - (skillOne?.correctCount ?? 0);
        }),
      );
      setCompletionFilter(false);
    } else {
      setResultList(storedResultList);
      setCompletionFilter(false);
    }
  };

  const exportCSV = async () => {
    if (candidateStatus) {
      const requestBody = {
        assessmentId: assessmentId,
        candidateStatus: candidateStatus,
      };
      try {
        const response = await exportCandidateCSV(requestBody);
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${candidateStatus}-candidates.csv`;
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        } else {
          const errorData = await response.json();
          toast.error(errorData.error, { position: 'bottom-center' });
        }
      } catch (error) {
        toast.error('Error sending mail: ' + error, {
          position: 'bottom-center',
        });
      }
    }
  };

  const applyFilter = async () => {
    try {
      // Reset to first page and fetch with filters
      setPageNumber(1);
      await fetchAssessmentUsers(1, sort, filters);
      setFilterOpen(false); // Close drawer after applying
    } catch (error) {
      toast.error('Filter not updated: ' + error, {
        position: 'bottom-center',
      });
    }
  };

  const resetFilter = async () => {
    setFilters([]);
    setPageNumber(1);
    await fetchAssessmentUsers(1, sort, []);
  };

  // Applied filters for display
  const appliedFilters = filters.reduce<Record<string, string[]>>(
    (acc, filter) => {
      // Find the question text from preScreenQuestions
      const question = preScreenQuestions?.find(
        (q) => q.questionId === filter.questionId,
      );
      const questionKey = question?.question || filter.questionId;

      if (filter.filterChoices && filter.filterChoices.length > 0) {
        acc[questionKey] = filter.filterChoices;
      } else if (
        filter.filterNumber &&
        (filter.filterNumber.min !== undefined ||
          filter.filterNumber.max !== undefined)
      ) {
        const rangeText: string[] = [];
        if (filter.filterNumber.min !== undefined)
          rangeText.push(`Min: ${filter.filterNumber.min}`);
        if (filter.filterNumber.max !== undefined)
          rangeText.push(`Max: ${filter.filterNumber.max}`);
        acc[questionKey] = rangeText;
      }
      return acc;
    },
    {},
  );

  // Handle clearing all filters
  const handleClearFilters = () => {
    resetFilter();
  };

  // Handle removing a specific filter
  const handleRemoveFilter = (filterId: string) => {
    const updatedFilters = filters.filter(
      (filter) => filter.questionId !== filterId,
    );

    setFilters(updatedFilters);
    // Apply the updated filters
    applyFilter();
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  interface PreScreenAnswer {
    question: string;
    answer: string | string[];
    questionId: string;
  }

  interface UserWithPreScreen extends UserAssessmentResponse {
    preScreenAnswers?: PreScreenAnswer[];
  }

  // Get filtered data for the current tab - now returns server data with frontend search filter
  const getFilteredUsers = () => {
    let results = serverUserResults;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      results = results.filter((item) => {
        const fullName = `${item.user.firstName || ''} ${
          item.user.lastName || ''
        }`.toLowerCase();
        const email = (item.user.email || '').toLowerCase();
        return fullName.includes(query) || email.includes(query);
      });
    }

    return results;
  };

  const handleBulkMove = async (option: string) => {
    const filteredData = getFilteredUsers();
    const transformedData = transformUserResultsToApiFormat(filteredData);
    const selectedUsers = transformedData.filter((user) =>
      selectedItems.includes(user.userId),
    );

    try {
      // Build candidateStatus array for the API payload
      const candidateStatusArray = selectedUsers.map((user) => ({
        status: option,
        userId: user.userId,
      }));

      // Prepare request body for candidate status update
      const requestBody = {
        assessmentId: assessmentId,
        candidateStatus: candidateStatusArray,
      };

      // Call the candidate status API
      const response = await setCandidateStatus(requestBody);

      if (response.ok) {
        // Update the UI after successful API call
        if (updateAssessmentUserList) {
          const assessmentUsers = selectedUsers.map((user) => ({
            assessmentUserId: user.id,
            userId: user.userId,
            firstName: user.user.firstName,
            lastName: user.user.lastName,
            email: user.user.email,
            enabled: user.enabled,
            score: user.score,
            status: user.status,
            marked: user.marked,
            candidateStatus: user.candidateStatus,
            assessmentId: user.assessmentId,
            maxScore: user.maxScore,
            startTime: user.startTime,
            endTime: user.endTime,
            totalScore: user.totalScore,
            behaviorScore: user.behaviorScore,
            preScreenCompleted: user.preScreenCompleted,
            skills: user.skills,
          }));
          updateAssessmentUserList(assessmentUsers, option as CandidateStatus);
        }

        setSelectedItems([]);
        setBulkActionOpen(false);
        toast.success(
          `Successfully moved ${selectedUsers.length} candidate(s) to ${option}`,
          {
            position: 'bottom-center',
          },
        );
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Error updating candidate status', {
          position: 'bottom-center',
        });
      }
    } catch (error) {
      toast.error('Error updating candidate status', {
        position: 'bottom-center',
      });
    }
  };

  const handleSendUnsuccessfulEmails = async () => {
    const filteredData = getFilteredUsers();
    const transformedData = transformUserResultsToApiFormat(filteredData);
    const selectedUsers = transformedData.filter((user) =>
      selectedItems.includes(user.userId),
    );

    // Filter only unsuccessful candidates
    const unsuccessfulUsers = selectedUsers.filter(
      (user) => user.candidateStatus === 'Unsuccessful',
    );

    if (unsuccessfulUsers.length === 0) {
      toast.warning('No unsuccessful candidates selected', {
        position: 'bottom-center',
      });
      return;
    }

    try {
      const emailRequestBody = {
        assessmentId: assessmentId,
        candidateStatus: 'Unsuccessful' as CandidateStatus,
      };

      const emailResponse = await sendCandidateEmail(emailRequestBody);

      if (emailResponse.ok) {
        const data = await emailResponse.json();
        toast.success(
          data.status ||
            `Successfully sent emails to ${unsuccessfulUsers.length} unsuccessful candidate(s)`,
          { position: 'bottom-center' },
        );
      } else {
        const emailErrorData = await emailResponse.json();
        toast.error(emailErrorData.error || 'Failed to send emails', {
          position: 'bottom-center',
        });
      }
    } catch (error) {
      toast.error('Error sending emails to unsuccessful candidates', {
        position: 'bottom-center',
      });
    }
  };

  const handleToggleSelectionDropdown = () => {
    setSelectionDropdownOpen(!selectionDropdownOpen);
  };

  const handleToggleBulkActionDropdown = () => {
    setBulkActionOpen(!bulkActionOpen);
  };

  return {
    calculateAverageTime,
    resultListData,
    selectedRows,
    skillVisibility,
    setSkillVisibility,
    setSelectedRows,
    transformUserResultsToApiFormat,
    exportCSV,
    onPageChange,
    handleSortChange,
    hasFetchedOnce,
    totalRecords,
    pageNumber,
    totalPages,
    history,
    sort,
    filterDrawer,
    setFilterDrawer,
    setFilters,
    filters,
    applyFilter,
    resetFilter,
    getFilteredUsers,
    appliedFilters,
    handleClearFilters,
    handleRemoveFilter,
    selectionDropdownOpen,
    setSelectionDropdownOpen,
    selectedItems,
    setSelectedItems,
    handleSelectAllItems,
    bulkActionOpen,
    setBulkActionOpen,
    handleToggleSelectionDropdown,
    handleToggleBulkActionDropdown,
    handleBulkMove,
    handleSendUnsuccessfulEmails,
    onResultClick, // Pass through the custom handler
    toggleAllSkills,
    toggleSkillVisibility,
    showAllSkills,
    visibleSkills,
    loading, // Add loading state
    serverUserResults, // Add server data
    filteredResults: getFilteredUsers(),
    serverTotalCount, // Add server total count
    fetchAssessmentUsers, // Add fetch function for manual refreshes
    handleCloseFilter,
    filterOpen,
    handleOpenFilter,
    searchTerm,
    setSearchTerm,
  };
};
